<template>
  <div class="analytics-tab">
    <!-- Modern Header -->
    <div class="tab-header">
      <div class="header-content">
        <div class="header-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        <div>
          <h2>Analytics e Métricas</h2>
          <p>Monitore acessos, desempenho e comportamento dos usuários</p>
        </div>
      </div>
      <div class="header-actions">
        <button @click="refreshData" class="refresh-btn" :disabled="loading">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            :class="{ 'animate-spin': loading }"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
          <span>Atualizar</span>
        </button>
      </div>
    </div>

    <!-- Time Period Selector -->
    <div class="period-selector">
      <button
        v-for="period in periods"
        :key="period.value"
        @click="selectedPeriod = period.value"
        :class="['period-btn', { active: selectedPeriod === period.value }]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
            clip-rule="evenodd"
          />
        </svg>
        <span>{{ period.label }}</span>
      </button>
    </div>

    <!-- Mock Data Warning -->
    <div v-if="usingMockData" class="mock-warning" style="background: #fef3cd; border: 1px solid #ffc107; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; color: #856404; font-size: 14px;">
      <span>⚠️</span>
      <span>Dados indisponíveis da API — exibindo dados estimados para demonstração.</span>
    </div>

    <!-- Key Stats Grid -->
    <div class="stats-grid" v-if="!loading && analytics">
      <div class="stat-card">
        <div class="stat-icon views">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fill-rule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Total de Visualizações</span>
          <span class="stat-value">{{
            formatNumber(analytics.totalViews)
          }}</span>
          <span class="stat-change positive" v-if="analytics.viewsChange > 0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clip-rule="evenodd"
              />
            </svg>
            +{{ analytics.viewsChange }}%
          </span>
          <span
            class="stat-change negative"
            v-else-if="analytics.viewsChange < 0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 13a1 1 0 100 2h5a1 1 0 001-1V9a1 1 0 10-2 0v2.586l-4.293-4.293a1 1 0 00-1.414 0L8 9.586 3.707 5.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0L11 9.414 14.586 13H12z"
                clip-rule="evenodd"
              />
            </svg>
            {{ analytics.viewsChange }}%
          </span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon users">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
            />
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Visitantes Únicos</span>
          <span class="stat-value">{{
            formatNumber(analytics.uniqueVisitors)
          }}</span>
          <span
            class="stat-change positive"
            v-if="analytics.visitorsChange > 0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clip-rule="evenodd"
              />
            </svg>
            +{{ analytics.visitorsChange }}%
          </span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon clicks">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Taxa de Cliques</span>
          <span class="stat-value">{{ analytics.clickRate }}%</span>
          <span
            class="stat-change positive"
            v-if="analytics.clickRateChange > 0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clip-rule="evenodd"
              />
            </svg>
            +{{ analytics.clickRateChange }}%
          </span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon time">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Tempo Médio</span>
          <span class="stat-value">{{ formatTime(analytics.avgTime) }}</span>
          <span class="stat-change positive" v-if="analytics.timeChange > 0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clip-rule="evenodd"
              />
            </svg>
            +{{ analytics.timeChange }}%
          </span>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-section" v-if="!loading && analytics">
      <!-- Views Chart -->
      <div class="chart-card full-width">
        <div class="chart-header">
          <div>
            <h3>Visualizações ao Longo do Tempo</h3>
            <p>Acessos diários nos últimos {{ periodLabel }}</p>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <span class="legend-dot views"></span>
              <span>Visualizações</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot visitors"></span>
              <span>Visitantes</span>
            </div>
          </div>
        </div>
        <div class="chart-container">
          <canvas ref="viewsChart"></canvas>
        </div>
      </div>

      <!-- Top Content Grid -->
      <div class="chart-card">
        <div class="chart-header">
          <h3>Posts Mais Visitados</h3>
          <p>Top 5 posts por visualizações</p>
        </div>
        <div class="top-list">
          <div
            v-for="(post, index) in analytics.topPosts || []"
            :key="post.id"
            class="top-item"
          >
            <div class="top-rank" :class="'rank-' + (index + 1)">
              {{ index + 1 }}
            </div>
            <div class="top-content">
              <h4>{{ post.title }}</h4>
              <p>{{ post.theme }}</p>
            </div>
            <div class="top-stat">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fill-rule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ formatNumber(post.views) }}</span>
            </div>
          </div>
          <div
            v-if="!analytics.topPosts || analytics.topPosts.length === 0"
            class="empty-list"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p>Nenhum dado disponível</p>
          </div>
        </div>
      </div>

      <!-- Top Users -->
      <div class="chart-card">
        <div class="chart-header">
          <h3>Usuários Mais Ativos</h3>
          <p>Top 5 usuários por engajamento</p>
        </div>
        <div class="top-list">
          <div
            v-for="(user, index) in analytics.topUsers || []"
            :key="user.id"
            class="top-item"
          >
            <div class="top-rank" :class="'rank-' + (index + 1)">
              {{ index + 1 }}
            </div>
            <div class="top-content">
              <h4>{{ user.username }}</h4>
              <p>{{ user.role === "admin" ? "Administrador" : "Usuário" }}</p>
            </div>
            <div class="top-stat">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"
                />
              </svg>
              <span>{{ formatNumber(user.activity_count) }}</span>
            </div>
          </div>
          <div
            v-if="!analytics.topUsers || analytics.topUsers.length === 0"
            class="empty-list"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p>Nenhum dado disponível</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Carregando analytics...</p>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !analytics" class="empty-state">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <h3>Dados de Analytics Indisponíveis</h3>
      <p>Não foi possível carregar os dados de analytics</p>
      <button @click="refreshData" class="retry-btn">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
            clip-rule="evenodd"
          />
        </svg>
        <span>Tentar Novamente</span>
      </button>
    </div>
  </div>
</template>

<script>
import apiService from "@/services/api";
import Chart from "chart.js/auto";

export default {
  name: "AnalyticsTab",
  data() {
    return {
      loading: false,
      analytics: null,
      usingMockData: false,
      selectedPeriod: 7,
      periods: [
        { value: 7, label: "7 dias" },
        { value: 14, label: "14 dias" },
        { value: 30, label: "30 dias" },
        { value: 90, label: "90 dias" },
      ],
      viewsChart: null,
    };
  },
  computed: {
    periodLabel() {
      const period = this.periods.find((p) => p.value === this.selectedPeriod);
      return period ? period.label : "";
    },
  },
  watch: {
    selectedPeriod() {
      this.loadAnalytics();
    },
  },
  mounted() {
    this.loadAnalytics();
  },
  beforeUnmount() {
    if (this.viewsChart) {
      this.viewsChart.destroy();
    }
  },
  methods: {
    async loadAnalytics() {
      this.loading = true;
      try {
        const response = await apiService.get(
          "/api/handlers?module=admin&action=stats-advanced",
          {
            params: { days: this.selectedPeriod },
          }
        );

        this.analytics = response.data;
        this.usingMockData = false;

        // Wait for next tick to ensure canvas is rendered
        await this.$nextTick();
        this.renderViewsChart();
      } catch (error) {
        console.error("Erro ao carregar analytics:", error);
        // Use mock data when API is not available
        this.analytics = this.generateMockAnalytics();
        this.usingMockData = true;
        await this.$nextTick();
        this.renderViewsChart();
      } finally {
        this.loading = false;
      }
    },

    generateMockAnalytics() {
      const days = this.selectedPeriod;
      const viewsTimeline = Array.from({ length: days }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (days - i - 1));
        return {
          date: date.toISOString().split("T")[0],
          views: Math.floor(Math.random() * 500) + 200,
          visitors: Math.floor(Math.random() * 300) + 100,
        };
      });

      return {
        totalViews: viewsTimeline.reduce((sum, d) => sum + d.views, 0),
        totalVisitors: viewsTimeline.reduce((sum, d) => sum + d.visitors, 0),
        viewsChange: Math.floor(Math.random() * 30) - 10,
        visitorsChange: Math.floor(Math.random() * 25) - 5,
        avgClickRate: (Math.random() * 5 + 2).toFixed(1),
        clickRateChange: (Math.random() * 2 - 1).toFixed(1),
        avgTimeOnSite: Math.floor(Math.random() * 180) + 120,
        timeChange: Math.floor(Math.random() * 60) - 30,
        viewsTimeline,
        topPosts: [
          {
            id: 1,
            title: "Introdução ao JavaScript Moderno",
            theme: "Programação",
            views: 1543,
          },
          {
            id: 2,
            title: "Como Criar APIs REST",
            theme: "Backend",
            views: 1234,
          },
          {
            id: 3,
            title: "Vue.js: Guia Completo",
            theme: "Frontend",
            views: 987,
          },
          {
            id: 4,
            title: "Docker para Iniciantes",
            theme: "DevOps",
            views: 856,
          },
          {
            id: 5,
            title: "Segurança em Aplicações Web",
            theme: "Segurança",
            views: 743,
          },
        ],
        topUsers: [
          {
            id: 1,
            username: "admin",
            role: "admin",
            activity_count: 245,
          },
          {
            id: 2,
            username: "david",
            role: "admin",
            activity_count: 189,
          },
          {
            id: 3,
            username: "user123",
            role: "user",
            activity_count: 156,
          },
          {
            id: 4,
            username: "developer",
            role: "user",
            activity_count: 134,
          },
          {
            id: 5,
            username: "reader",
            role: "user",
            activity_count: 98,
          },
        ],
      };
    },

    async refreshData() {
      await this.loadAnalytics();
    },

    renderViewsChart() {
      if (!this.$refs.viewsChart || !this.analytics?.viewsTimeline) return;

      // Destroy existing chart
      if (this.viewsChart) {
        this.viewsChart.destroy();
      }

      const ctx = this.$refs.viewsChart.getContext("2d");
      const timeline = this.analytics.viewsTimeline;

      this.viewsChart = new Chart(ctx, {
        type: "line",
        data: {
          labels: timeline.map((d) => this.formatChartDate(d.date)),
          datasets: [
            {
              label: "Visualizações",
              data: timeline.map((d) => d.views),
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: "#3b82f6",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
            },
            {
              label: "Visitantes",
              data: timeline.map((d) => d.visitors),
              borderColor: "#10b981",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: "#10b981",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              titleColor: "#ffffff",
              bodyColor: "#ffffff",
              padding: 12,
              borderColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 1,
              displayColors: true,
              boxPadding: 6,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: "rgba(0, 0, 0, 0.05)",
                drawBorder: false,
              },
              ticks: {
                color: "#6b7280",
                padding: 10,
              },
            },
            x: {
              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                color: "#6b7280",
                padding: 10,
              },
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
        },
      });
    },

    formatNumber(num) {
      if (!num) return "0";
      if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
      if (num >= 1000) return (num / 1000).toFixed(1) + "K";
      return num.toString();
    },

    formatTime(seconds) {
      if (!seconds) return "0s";
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    },

    formatChartDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
      });
    },
  },
};
</script>

<style scoped>
/* Modern Analytics Tab Styles */
.analytics-tab {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.tab-header {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: #3b82f6;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.header-icon svg {
  width: 32px;
  height: 32px;
  color: white;
}

.tab-header h2 {
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.tab-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn svg {
  width: 18px;
  height: 18px;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Period Selector */
.period-selector {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.period-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.period-btn svg {
  width: 16px;
  height: 16px;
}

.period-btn:hover {
  background: white;
  border-color: #d1d5db;
  color: #374151;
}

.period-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border-color: #3b82f6;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 28px;
  height: 28px;
  color: white;
}

.stat-icon.views {
  background: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.stat-icon.users {
  background: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.stat-icon.clicks {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.stat-icon.time {
  background: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.stat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  line-height: 1;
}

.stat-change {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  width: fit-content;
}

.stat-change svg {
  width: 14px;
  height: 14px;
}

.stat-change.positive {
  background: #d1fae5;
  color: #065f46;
}

.stat-change.negative {
  background: #fee2e2;
  color: #991b1b;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.chart-card {
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px solid #e5e7eb;
  animation: slideIn 0.5s ease-out;
}

.chart-card.full-width {
  grid-column: 1 / -1;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.chart-header h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.chart-header p {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}

.chart-legend {
  display: flex;
  gap: 1.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 500;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-dot.views {
  background: #3b82f6;
}

.legend-dot.visitors {
  background: #10b981;
}

.chart-container {
  height: 300px;
  position: relative;
}

/* Top Lists */
.top-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.top-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.top-item:hover {
  background: white;
  border-color: #3b82f6;
  transform: translateX(4px);
}

.top-rank {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 8px;
  flex-shrink: 0;
}

.top-rank.rank-1 {
  background: #fbbf24;
  color: white;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.top-rank.rank-2 {
  background: #9ca3af;
  color: white;
  box-shadow: 0 2px 8px rgba(156, 163, 175, 0.3);
}

.top-rank.rank-3 {
  background: #f59e0b;
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.top-rank.rank-4,
.top-rank.rank-5 {
  background: #e5e7eb;
  color: #6b7280;
}

.top-content {
  flex: 1;
  min-width: 0;
}

.top-content h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-content p {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}

.top-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #6b7280;
}

.top-stat svg {
  width: 16px;
  height: 16px;
}

.empty-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-list svg {
  width: 48px;
  height: 48px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-list p {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0;
}

/* Loading State */
.loading-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

.loading-state p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

/* Empty State */
.empty-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px dashed #e5e7eb;
}

.empty-state svg {
  width: 64px;
  height: 64px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0 0 1.5rem 0;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.retry-btn:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.retry-btn svg {
  width: 18px;
  height: 18px;
}

/* Responsive */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .period-selector {
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .tab-header {
    flex-direction: column;
    gap: 1rem;
  }

  .header-content,
  .header-actions {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .chart-header {
    flex-direction: column;
    gap: 1rem;
  }

  .chart-legend {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>
